import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MenuStages } from '../constants/enums';
import { CartItemAdditionTypes } from '../constants/event';
import {
  Category,
  CategoryAndTimePeriodQueryQuery,
  MenuItem,
  MenuItemSetting,
  MenuItemsQueryQuery,
  MenuOverride,
  ModalityType,
  ModifierGroup,
  ModifierGroupsQueryQuery,
  PosProperties,
  TimePeriod,
} from '../generated-interfaces/graphql';
import { startLoading } from '../redux/features/loading/loading.slice';
import { IUnavailableItem } from '../types';
import { defaultMenuVersion } from '../utils/constants';
import {
  findModGroup,
  getModifierAndModGroupFromHypotheses,
  getModifierAndModGroupFromHypothesesByName,
  resolveHypothesisModGroupId,
} from '../utils/hypotheses';
import { getOptionModifierGroups } from '../utils/lazyOptionBuild';
import {
  clampModifierQuantity,
  getModifierMaxQuantity,
  isSplitSlotGroup,
} from '../utils/modifierQuantity';
import logger from '../utils/logger';
import { isExpressModeActive } from '../utils/expressMode';
import {
  selectAIUpsellSuppression,
  selectIsAIExpressUpsellSuppressionActive,
  selectIsExpressTimerActive,
} from '../selectors/menu';
import { selectIsSelectedItemUnopenable } from '../redux/features/autoCombo/autoCombo.selector';
import {
  selectIsLazyOptionBuildEnabled,
  selectIsMenuItemBuildCacheEnabled,
  selectIsModifierQuantityEnabled,
  selectIsShadowModeEnabled,
} from '../redux/features/config/config.selector';
import { ModSymbolCodeNameMappingType } from '../utils/mappings';
import {
  buildFullMenuItem,
  considerTimePeriodCategory,
  fetchBrandMenu,
  fetchBrandMenuV2,
  fetchMenuBasedOnStage,
  findCartItemInMenuGraphByName,
  formatCategoryAndTimePeriodResponse,
  formatMenuResponse,
  formatModGroupResponse,
  getComboPrpIdToALaCartePrpId,
  getEligibleAutoComboItem,
  getParentModifier,
  IMenuVersion,
  IMenuVersionsResponse,
  MenuResponses,
  parseCategoryAndTimeperiodResponse,
  ParsedCategory,
  parseMenuResponse,
  PersistentMenuProperty,
  TopLevelMenuItem,
} from '../utils/menu';
import { getMenuVersionsFromMenuAPI } from '../utils/network';
import { getConsolidatedMenuBrand } from '../utils/restaurants';
import { GenericMap } from '../utils/types';
import {
  addHypothesisCartItem,
  cartActions,
  IBatchedModifierSelection,
  setSequenceId,
} from './cartSlice';
// HITL-1617. Note the import cycle this joins: `cartSlice` <-> `menuSlice`
// already exists (cartSlice.ts:27 reads `selectMenuVersion.fulfilled`), and
// `draftCart.slice` reads the same action for the same reason. Every binding
// used here is read at dispatch time, never at module-evaluation time.
import {
  CartScope,
  MAIN_CART_SCOPE,
} from '../redux/features/draftCart/cartScope';
import {
  cartActionsForScope,
  cartItemsForScope,
  cartStateForScope,
  selectNewItemCartScope,
} from '../redux/features/draftCart/cartScope.routing';
import { dialogActions } from './dialogSlice';
import {
  AgentInterceptionType,
  EntityMenuItem,
  messagingActions,
} from './messagingSlice';
import {
  CartItem,
  getCartItemNonDummyNode,
  getValidCartItems,
  processDefaultSelectedModifiers,
  selectModifierInCartItem,
} from '../utils/cart';
import { sendSpokenPrompt } from './spokenPromptThunk';
import {
  resetAutoComboState,
  setAutoCombinedCart,
} from '../redux/features/autoCombo/autoCombo.slice';
import { sendOrder } from './orderSlice';
import {
  getAuthRefactorFeatureFlag,
  getMenuUnificationFeatureFlag,
} from '../utils/session-storage';
import { processBrandMenu } from '../utils/menuUnification';
import { withInfoSlashCommands } from '../utils/infoSlashCommands';
import { InfoSlashCommandOptions } from '../utils/infoSlashCommands.options';
import { selectInfoSlashCommandOptions } from '../redux/features/config/config.selector';
import {
  IBrandMenu,
  IBrandUnavailableEntity,
  IBusinessRule,
  IConstraint,
  IItemConversionBusinessRules,
  IMUItem,
  IRestaurantMealPeriod,
  IRestaurantUnavailableEntity,
  IUtterance,
} from '../types/brandMenu';
import { ParsedMenuItem } from '../types/menu';
import {
  buildSubstituteItem,
  buildFullMenuUnifiedItem,
  buildIntentCardsFromBM,
  findNonTopLevelMenuItem,
  getDismissReadbackPhrasesFromBM,
  getTopLevelItems,
  isBMItemInformationalOnly,
  isBMItemNotSold,
  isBMItemOutOfStock,
  sanitizeHypothesisOrderItem,
} from '../utils/menuUnification.utils';
import { IntentCard } from '../types/intent';
import { upsell } from '../utils/upsell';
import { UpsellTriggers } from '../components/OmniBar/Omnibar.constants';
import { AUTO_COMBOS, UPSELL_METRIC_VERSION } from '../constants';
import { handleGreetingUpsell } from './menuSlice.utils';
import {
  selectSlashCommandSpokenPrompt,
  selectSpokenPrompt,
  selectUpsellExperimentStamp,
  selectWithheldConstraintNames,
  selectWithheldConstraints,
} from '../redux/features/experiments/experiments.selector';
import {
  isConstraintWithheld,
  logWithheldPromptSkipped,
  slashCommandPromptSlot,
} from '../redux/features/experiments/experiments.utils';
import { IPromptSlot } from '../redux/features/experiments/experiments.props';
import { promptAsked } from '../redux/features/experiments/experiments.slice';
import {
  initialItemConversionBusinessRules,
  LoadedVersionDetails,
} from './menuSlice.props';
import { getMenuVersion } from '../utils/menuVersion';
import { convertToMapUsingName } from '../utils';
import { analyzeOptionReplacement } from '../utils/optionReplacement';
import { ConversionSize } from '../utils/itemConversion.constants';
import { updatePrimaryRestaurantCode } from './restaurantSlice';

/**
 * Yields control back to the main thread so the browser can paint
 * pending UI updates (e.g. the loading spinner) before heavy
 * synchronous processing begins. Uses MessageChannel for minimal
 * latency (fires before setTimeout), with setTimeout(0) as fallback.
 */
const yieldToMain = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof MessageChannel !== 'undefined') {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => resolve();
      channel.port2.postMessage(undefined);
    } else {
      setTimeout(resolve, 0);
    }
  });

export enum MenuLoadSource {
  CACHED = 'cached',
  DOWNLOADED = 'downloaded',
}

export interface MenuLoadingTimings {
  downloadStartTime?: number;
  downloadEndTime?: number;
  downloadDurationMs?: number;
  processingStartTime?: number;
  processingEndTime?: number;
  processingDurationMs?: number;
  source?: MenuLoadSource;
  unleashFlagDurationMs?: number;
}

export interface MenuState {
  topLevelMenuItems: GenericMap<TopLevelMenuItem>;
  availableCategoryWithTimePeriod: ParsedCategory[];
  categoriesWithTimePeriod: ParsedCategory[];
  alwaysAvailableCategories: ParsedCategory[];
  menuRes?: MenuResponses;
  fullMenuItems: GenericMap<ParsedMenuItem>;
  persistentVoiceProps: GenericMap<PersistentMenuProperty>;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  codeNameMapping: ModSymbolCodeNameMappingType;
  menuVersions: IMenuVersion[];
  selectedMenuVersion: string;
  prodLiveVersion?: IMenuVersion;
  unavailableItems: Record<string, IUnavailableItem>;
  autoComboPrpIds: string[];
  comboPrpIdToALaCartePrpId: Record<string, string>;
  brandMenu?: IBrandMenu;
  brandMenuItemsByName: Record<string, IMUItem>;
  itemConversionBusinessRules: IItemConversionBusinessRules;
  restaurantUnavailableEntities: IRestaurantUnavailableEntity[];
  unavailableItemsByName: Record<
    string,
    Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
  >;
  bmItemsSynonyms?: Record<string, Array<string>>;
  loadedVersionDetails: LoadedVersionDetails | null;
  restaurantMealPeriodsByName: Record<string, IRestaurantMealPeriod>;
  currentRestaurantGroups: string[];
  greetingDetails?: {
    item: any;
    constraint: IConstraint;
  };
  menuLoadingTimings: MenuLoadingTimings;
  intentCards?: IntentCard[];
  dismissReadbackPhrases?: string[];
}

export interface IAddItemToCart {
  menuItem: TopLevelMenuItem;
  quantity?: number;
  addedBy?: CartItemAdditionTypes;
  prefixWord?: string;
  inputModSymbol?: string;
  buildViaAutoCombo?: boolean;
  byPassAutoPrompt?: boolean;
  // With byPassAutoPrompt: the phrase the caller already spoke, recorded on the upsell attempt.
  spokenPrompt?: string;
  /**
   * HITL-1619 — which cart this item is being added to. Omit it and the scope is
   * resolved from the agent's mode, which is what every human caller wants; an
   * `addedBy: AI` add forces the main cart regardless, because that is the AI's
   * own replay and it always rebuilds the order.
   */
  cartScope?: CartScope;
  /**
   * HITL-1700 — the upsell_id an already-spoken prompt carried. A caller that
   * speaks first and adds with `byPassAutoPrompt` passes it so the attempt this
   * thunk creates matches the prompt's TTS acknowledgement; omit it and a fresh
   * id is minted.
   */
  upsellId?: string;
  /**
   * HITL-1701 — the upsell is the AI's, not the agent's: an accepted copilot
   * suggestion. The AI records the attempt and the customer's answer itself, so
   * no HITL attempt is created — with none in memory, the agent's later Yes/No
   * tick and the implicit settle() record nothing either.
   */
  skipUpsellAttempt?: boolean;
}
export interface IComboItemMatch {
  autoComboPrpId: string;
  modifierGroupId: string;
  modifierPrpId: string;
  cartItemId: number;
  nonDummyItemPrpId?: string;
  nonDummyCartItemPrpId?: number;
}

export interface IProcessedCategoryAndTimePeriodJSON {
  categories: GenericMap<Category>;
  timePeriods: GenericMap<TimePeriod>;
}

export interface IProcessedMenuJSON {
  menuItems: GenericMap<MenuItem>;
  overrides: GenericMap<MenuOverride>;
  menuItemSettings: GenericMap<MenuItemSetting>;
  posSettings: GenericMap<PosProperties>;
}

export interface IProcessedModGroupsJSON {
  modifierGroups: GenericMap<ModifierGroup>;
}
export interface IUpdateMenuItems {
  topLevelMenuItems: GenericMap<TopLevelMenuItem>;
  processedCategoryAndTimePeriodJSON: GenericMap<IProcessedCategoryAndTimePeriodJSON>;
  processedMenuJSON: GenericMap<IProcessedMenuJSON>;
  processedModGroupsJSON: GenericMap<IProcessedModGroupsJSON>;
  categoriesWithTimePeriod: ParsedCategory[];
  availableCategoryWithTimePeriod: ParsedCategory[];
  alwaysAvailableCategories: ParsedCategory[];
  persistentVoiceProps: GenericMap<PersistentMenuProperty>;
  codeNameMapping: ModSymbolCodeNameMappingType;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  unavailableItems: Record<string, IUnavailableItem>;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  currentRestaurantGroups?: string[];
  itemConversionBusinessRules?: IItemConversionBusinessRules;
  brandMenu?: IBrandMenu;
  bmItemsSynonyms?: Record<string, Array<string>>;
  restaurantUnavailableEntities?: IRestaurantUnavailableEntity[];
  unavailableItemsByName?: Record<
    string,
    Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
  >;
  restaurantCode?: string;
}

export const initialState: MenuState = {
  topLevelMenuItems: {},
  availableCategoryWithTimePeriod: [],
  categoriesWithTimePeriod: [],
  alwaysAvailableCategories: [],
  menuRes: undefined,
  fullMenuItems: {},
  persistentVoiceProps: {},
  modSymbolMapping: {},
  codeNameMapping: {},
  menuVersions: [],
  selectedMenuVersion: 'latest',
  prodLiveVersion: {} as IMenuVersion,
  unavailableItems: {},
  autoComboPrpIds: [],
  comboPrpIdToALaCartePrpId: {},
  brandMenu: undefined,
  brandMenuItemsByName: {},
  itemConversionBusinessRules: initialItemConversionBusinessRules,
  bmItemsSynonyms: undefined,
  restaurantUnavailableEntities: [],
  unavailableItemsByName: {},
  loadedVersionDetails: null,
  restaurantMealPeriodsByName: {},
  currentRestaurantGroups: [],
  greetingDetails: undefined,
  menuLoadingTimings: {},
  intentCards: undefined,
  dismissReadbackPhrases: undefined,
};

/**
 * This function takes the menu data returned from APIs and make it in usable fashion to be stored in redux state
 */
const processMenuResponse = ({
  categoryAndTimePeriodJSON,
  menuJSON,
  modifierGroupJSON,
  persistentMenuProperty,
  unavailableItems,
  timezone,
  currentStage,
  getState,
}: {
  categoryAndTimePeriodJSON: CategoryAndTimePeriodQueryQuery | {};
  menuJSON: MenuItemsQueryQuery | {};
  modifierGroupJSON: ModifierGroupsQueryQuery | {};
  persistentMenuProperty: PersistentMenuProperty[];
  unavailableItems: Record<string, IUnavailableItem>;
  timezone: string;
  currentStage: string;
  getState: Function;
}) => {
  const {
    restaurant: { selectedRestaurantCode } = { selectedRestaurantCode: '' },
  } = getState();
  let persistentVoiceProps = {};
  try {
    if (persistentMenuProperty?.length > 0) {
      Object.assign(
        persistentVoiceProps,
        (persistentMenuProperty as PersistentMenuProperty[]).reduce(
          (acc, entry) => {
            acc[entry.unique_identifier] = entry;
            return acc;
          },
          {} as GenericMap<PersistentMenuProperty>
        )
      );
    }
  } catch (error) {
    logger.warn({
      restaurantCode: selectedRestaurantCode,
      message: 'Failed while processing persistent menu properties',
      error,
    });
  }

  const processedCategoryAndTimePeriodJSON =
    formatCategoryAndTimePeriodResponse(
      categoryAndTimePeriodJSON as CategoryAndTimePeriodQueryQuery
    );

  const {
    categoriesWithTimePeriod,
    alwaysAvailableCategoryWithTimePeriods,
    autoComboPrpIds,
  } = parseCategoryAndTimeperiodResponse(processedCategoryAndTimePeriodJSON);

  const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
    categoriesWithTimePeriod,
    timezone
  );
  const availableCategoryWithTimePeriod = categoriesWithActiveTimePeriod.filter(
    (category) => !category.inactive
  );

  const alwaysAvailableCategories = considerTimePeriodCategory(
    alwaysAvailableCategoryWithTimePeriods,
    timezone
  );

  const processedMenuJSON = formatMenuResponse(menuJSON as MenuItemsQueryQuery);

  const { topLevelMenuItems, codeNameMapping, modSymbolMapping } =
    parseMenuResponse({
      categories: [
        ...categoriesWithActiveTimePeriod.filter(
          (category) => category.name !== AUTO_COMBOS
        ),
        ...alwaysAvailableCategories,
      ],
      menuRes: processedMenuJSON,
      persistentVoiceProps,
      unavailableItems,
      stage: currentStage,
    });

  const modality = getState().cart.modality;
  const processedModGroupsJSON = formatModGroupResponse(
    modifierGroupJSON as ModifierGroupsQueryQuery
  );

  const comboPrpIdToALaCartePrpId = getComboPrpIdToALaCartePrpId(
    autoComboPrpIds,
    {
      ...processedCategoryAndTimePeriodJSON,
      ...processedMenuJSON,
      ...processedModGroupsJSON,
    }
  );

  return {
    modality,
    topLevelMenuItems,
    processedCategoryAndTimePeriodJSON,
    processedMenuJSON,
    processedModGroupsJSON,
    categoriesWithTimePeriod,
    availableCategoryWithTimePeriod,
    alwaysAvailableCategories,
    persistentVoiceProps,
    codeNameMapping,
    modSymbolMapping,
    unavailableItems,
    autoComboPrpIds,
    comboPrpIdToALaCartePrpId,
    restaurantCode: selectedRestaurantCode,
    itemConversionBusinessRules: initialItemConversionBusinessRules,
  };
};

export const fetchMenu = createAsyncThunk(
  'menu/getMenu',
  async (
    {
      restaurantCode,
      timezone,
      currentMenuVersion,
      currentStage,
      logout,
      taskRouterMenuLoadFailureCallback,
      taskRouterMenuLoadSuccessCallback,
    }: {
      restaurantCode: string;
      timezone?: string;
      currentMenuVersion: string;
      currentStage: MenuStages;
      logout: Function;
      taskRouterMenuLoadFailureCallback?: Function;
      taskRouterMenuLoadSuccessCallback?: Function;
    },
    { getState, dispatch }
  ) => {
    dispatch(updateLoadedVersionDetails(null));
    dispatch(resetMenuLoadingTimings());

    try {
      if (getMenuUnificationFeatureFlag()) {
        // Track download time
        const downloadStartTime = performance.now();
        dispatch(
          updateMenuLoadingTimings({
            downloadStartTime,
            source: MenuLoadSource.DOWNLOADED,
          })
        );

        const state = getState();
        const {
          CONSOLIDATED_MENU_BRANDS,
          CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS,
        } = state.config;
        // Brand menu V2 activates only when a brand key can be derived (i.e.
        // CONSOLIDATED_MENU_BRANDS is set and this restaurant isn't excluded).
        // Otherwise fall back to the per-restaurant fetch (reference path).
        const brandKey = getConsolidatedMenuBrand(
          restaurantCode,
          CONSOLIDATED_MENU_BRANDS,
          CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS
        );

        const {
          brandMenu,
          restaurantUnavailableEntities,
          restaurantMealPeriods,
          restaurantCode: primaryRestaurantCode,
          utterancesPromise,
        } = brandKey
          ? await fetchBrandMenuV2({
              restaurantCode,
              brandKey,
              state,
              dispatch,
            })
          : await fetchBrandMenu({
              restaurantCode,
              state,
              dispatch,
            });

        // Persist the API-returned restaurant code so selectors can use it
        // for brand menu group lookups (mirroring scenarios).
        if (primaryRestaurantCode) {
          dispatch(updatePrimaryRestaurantCode(primaryRestaurantCode));
        }

        const downloadEndTime = performance.now();
        const downloadDurationMs = downloadEndTime - downloadStartTime;
        dispatch(
          updateMenuLoadingTimings({ downloadEndTime, downloadDurationMs })
        );

        // Yield to the main thread so the loading spinner can render
        // before the heavy synchronous processing blocks the thread
        await yieldToMain();

        // Track processing time
        const processingStartTime = performance.now();
        dispatch(updateMenuLoadingTimings({ processingStartTime }));

        const builtMenu = processBrandMenu(
          brandMenu,
          primaryRestaurantCode,
          restaurantUnavailableEntities,
          restaurantMealPeriods,
          timezone
        );
        // HITL-1749: the `/info_*` commands, when info_slash_commands is on.
        const processedMenu = {
          ...builtMenu,
          topLevelMenuItems: withInfoSlashCommands(
            builtMenu.topLevelMenuItems,
            selectInfoSlashCommandOptions(getState()),
            {
              brandMenuItems: brandMenu.items ?? [],
              unavailableItemsByName: builtMenu.unavailableItemsByName,
              timezone,
              restaurantMealPeriodsByName:
                builtMenu.restaurantMealPeriodsByName,
            }
          ),
        };

        const processingEndTime = performance.now();
        const processingDurationMs = processingEndTime - processingStartTime;
        dispatch(
          updateMenuLoadingTimings({ processingEndTime, processingDurationMs })
        );

        dispatch(updateMenuItems(processedMenu));
        taskRouterMenuLoadSuccessCallback?.();

        // Load intent cards asynchronously — not on the critical path
        if (utterancesPromise) {
          utterancesPromise.then((utterances) => {
            if (utterances?.length) {
              dispatch(updateIntentCards(utterances));
            }
          });
        }
      } else {
        // Track download time
        const downloadStartTime = performance.now();
        dispatch(
          updateMenuLoadingTimings({
            downloadStartTime,
            source: MenuLoadSource.DOWNLOADED,
          })
        );

        const {
          categoryAndTimePeriodJSON,
          menuJSON,
          modifierGroupJSON,
          persistentMenuProperty,
          unavailableItems,
        } = await fetchMenuBasedOnStage({
          restaurantCode,
          state: getState(),
          currentMenuVersion,
          currentStage,
          dispatch,
        });

        const downloadEndTime = performance.now();
        const downloadDurationMs = downloadEndTime - downloadStartTime;
        dispatch(
          updateMenuLoadingTimings({ downloadEndTime, downloadDurationMs })
        );

        // Yield to the main thread so the loading spinner can render
        // before the heavy synchronous processing blocks the thread
        await yieldToMain();

        // Track processing time
        const processingStartTime = performance.now();
        dispatch(updateMenuLoadingTimings({ processingStartTime }));

        const processedMenu = processMenuResponse({
          categoryAndTimePeriodJSON,
          menuJSON,
          modifierGroupJSON,
          persistentMenuProperty,
          unavailableItems,
          timezone: timezone || '',
          currentStage,
          getState,
        });

        const processingEndTime = performance.now();
        const processingDurationMs = processingEndTime - processingStartTime;
        dispatch(
          updateMenuLoadingTimings({ processingEndTime, processingDurationMs })
        );

        dispatch(updateMenuItems(processedMenu));
        taskRouterMenuLoadSuccessCallback?.();
      }
    } catch (error) {
      const {
        cachedMenu: { menusById },
        taskRouter: { currentTaskId },
        order: { currentSessionId: orderSessionId },
        messages: { currentSessionId: messageSessionId },
      } = getState();

      logger.error({
        restaurantCode,
        message: 'Get Menu Items From Restaurant Portal Failed',
        isTaskFromTaskRouter: !!currentTaskId,
        error: JSON.stringify(error),
      });

      //If fetching the menu of a restaurant fails, as a fallback mechanism the following things are done when task is arrived via task-router
      // - Old menu cache of restaurant exists => use the old version menu
      // - Old menu cache of restaurant does not exists => trigger an intervention with reason code, empty the menu, alert of menu load error(5 seconds), take them to the task router lobby

      if (currentTaskId) {
        //Task arrived via task router
        if (menusById?.[restaurantCode]) {
          //Old menu cache exists for this restaurant
          logger.warn({
            restaurantCode,
            message: 'Using old cache of the restaurant',
            isTR: true,
          });
          const {
            categoryAndTimePeriodJSON,
            menuJSON,
            modifierGroupJSON,
            persistentMenuProperty,
            unavailableItems,
          } = menusById[restaurantCode];
          const processedMenu = processMenuResponse({
            categoryAndTimePeriodJSON,
            menuJSON,
            modifierGroupJSON,
            persistentMenuProperty,
            unavailableItems,
            timezone: timezone || '',
            currentStage,
            getState,
          });
          dispatch(updateMenuItems(processedMenu));
          taskRouterMenuLoadSuccessCallback?.();
        } else {
          //No old cache exists for the restaurant
          logger.error({
            restaurantCode,
            message: 'No old cache exists for the restaurant',
            isTR: true,
          });
          taskRouterMenuLoadFailureCallback?.({
            restaurantCode,
            orderSessionId,
            messageSessionId,
            logout,
          });
        }
      } else if (getAuthRefactorFeatureFlag()) {
        logout('Error while fetching menu. Logging out');
      }
    }

    const versionDetails = getMenuVersion(getState);
    logger.debug({
      messsage: `Set loaded menu version details`,
      versionDetails,
    });
    dispatch(updateLoadedVersionDetails(versionDetails));
  }
);

export const formAutoCombos = createAsyncThunk(
  '',
  async (_, { dispatch, getState }) => {
    try {
      const {
        menu: {
          menuRes,
          topLevelMenuItems,
          modSymbolMapping,
          autoComboPrpIds,
          persistentVoiceProps,
          unavailableItems,
        },
        cart: { cartItems, cartItemsQuantity, sequenceId, modality },
        config: { FEATURE_FLAG_AUTO_COMBO },
        restaurant: { selectedStage },
      } = getState();
      if (!FEATURE_FLAG_AUTO_COMBO) return;
      if (!menuRes) return;

      const validCartItems = Object.values(getValidCartItems(cartItems));
      // Need at least two valid cart items to create a combo item so stop finding auto combo when there is no valid cart item or the only valid cart item with quantity less than 2
      if (
        !validCartItems.length ||
        (validCartItems.length === 1 &&
          cartItemsQuantity[validCartItems[0].cartItemId] < 2)
      ) {
        dispatch(resetAutoComboState());

        // Trigger sendOrder after resetting auto combo state
        if (validCartItems.length) dispatch(sendOrder({}));
        return;
      }

      const autoCombinedCartItems = Object.assign({}, cartItems);
      const autoCombinedCartItemsQuantity = Object.assign(
        {},
        cartItemsQuantity
      );
      let currSequenceId = sequenceId;

      // Use while loop with explicit index control to allow restarting from beginning
      // when a combo is created (cart state changes)
      let i = 0;
      while (i < autoComboPrpIds.length) {
        // Need at least two valid cart items to create a combo item so stop finding auto combo when there is no valid auto combined cart item or one valid auto combined cart item with quantity less than 2
        const validAutoCombinedCartItems = getValidCartItems(
          autoCombinedCartItems
        );
        const validAutoCombinedCartItemsToArray = Object.values(
          validAutoCombinedCartItems
        );
        if (
          !validAutoCombinedCartItemsToArray.length ||
          (validAutoCombinedCartItemsToArray.length === 1 &&
            autoCombinedCartItemsQuantity[
              validAutoCombinedCartItemsToArray[0].cartItemId
            ] < 2)
        )
          break;

        const autoComboPrpId = autoComboPrpIds[i];
        const cartCopy = Object.assign({}, validAutoCombinedCartItems);
        const cartItemsQuantityCopy = Object.assign(
          {},
          autoCombinedCartItemsQuantity
        );
        const autoCombo = menuRes.menuItems[autoComboPrpId] || {};

        // Check the cart to find the eligible auto combo item
        const {
          isAutoComboEligible,
          comboItemMatches,
          isDummyItem,
          nonDummyItemMatch,
        } = getEligibleAutoComboItem(
          autoCombo,
          menuRes,
          autoComboPrpId,
          cartItemsQuantityCopy,
          cartCopy
        );

        if (isAutoComboEligible && comboItemMatches.length) {
          // populate the full menu graph
          const menuItem = topLevelMenuItems[autoComboPrpId];
          if (!menuItem) {
            i++; // Increment before continue to avoid infinite loop
            continue;
          }
          const parsedMenuItem = buildFullMenuItem(
            menuItem,
            menuRes,
            persistentVoiceProps,
            unavailableItems,
            selectedStage
          );
          const cartItem: CartItem = {
            ...parsedMenuItem,
            cartItemId: currSequenceId,
            childModifierGroups: {},
            modality,
            modcode: menuItem.modcode,
            addedBy: CartItemAdditionTypes.human,
            autoCombinedCartItemIds: [],
          };

          // Add the auto combo item to autoCombinedCartItems
          autoCombinedCartItems[currSequenceId] = cartItem;
          autoCombinedCartItemsQuantity[currSequenceId] = 1;

          let node = cartItem;
          if (isDummyItem && nonDummyItemMatch) {
            const nonDummyItemParentModGroup = Object.values(
              parsedMenuItem.modifierGroups
            )[0];
            const nonDummyItem = Object.values(parsedMenuItem.modifierGroups)[0]
              .menuItems[nonDummyItemMatch.id];
            // select the non-dummy item first and use it as the node to populate the items
            selectModifierInCartItem(
              cartItem,
              nonDummyItem.itemId,
              nonDummyItemParentModGroup.id,
              true,
              '',
              modSymbolMapping,
              menuRes.posSettings,
              modality
            );
            node =
              cartItem.childModifierGroups[nonDummyItemParentModGroup.id]
                ?.selectedItems[nonDummyItemMatch.id];
          }

          // Add combo items as modifiers and remove them from the cart
          comboItemMatches.forEach((match) => {
            autoCombinedCartItems[currSequenceId].autoCombinedCartItemIds?.push(
              match.cartItemId
            );

            const comboItemParentModGroup =
              node.modifierGroups[match.modifierGroupId];

            const comboItem =
              comboItemParentModGroup?.menuItems[match.modifierPrpId];
            if (!comboItemParentModGroup || !comboItem) {
              throw new Error(
                `Can not find matched combo item ${comboItem?.name} or matched parent modifier group ${comboItemParentModGroup?.name} when forming the auto combo`
              );
            }

            let pathToItem = comboItemParentModGroup.id;
            // Update the cartItem directly
            selectModifierInCartItem(
              node,
              comboItem.itemId,
              comboItemParentModGroup.id,
              true,
              '',
              modSymbolMapping,
              menuRes.posSettings,
              modality,
              pathToItem
            );

            const handleModifierMatch = (
              cartItemMatch: CartItem,
              comboItem: ParsedMenuItem,
              pathToItem: string
            ) => {
              const cartItemMatchChildrenModGroupIds = Object.keys(
                cartItemMatch.childModifierGroups
              );
              if (cartItemMatchChildrenModGroupIds.length) {
                const comboItemModGroupIds = Object.keys(
                  comboItem.modifierGroups
                );
                cartItemMatchChildrenModGroupIds.forEach((childModGroupId) => {
                  Object.values(
                    cartItemMatch.childModifierGroups[childModGroupId]
                      .selectedItems
                  ).forEach((cartItemSelectedItem) => {
                    let menuItemMatchId;
                    let parentModGroupMatchId;
                    if (comboItemModGroupIds.includes(childModGroupId)) {
                      // combo item and a la cart item are using the same modifier group
                      menuItemMatchId = cartItemSelectedItem.id;
                      parentModGroupMatchId = childModGroupId;
                    } else {
                      // combo item and a la cart item are not using the same modifier group. Try to match by name.
                      const { menuItemMatchByNameId, modGroupMatchByNameId } =
                        findCartItemInMenuGraphByName(
                          cartItemSelectedItem,
                          comboItem
                        );
                      menuItemMatchId = menuItemMatchByNameId;
                      parentModGroupMatchId = modGroupMatchByNameId;
                    }
                    if (menuItemMatchId && parentModGroupMatchId) {
                      const modGroup =
                        comboItem.modifierGroups[parentModGroupMatchId];
                      const menuItem = modGroup.menuItems[menuItemMatchId];
                      const pathToModifier = [
                        pathToItem,
                        comboItem.id,
                        modGroup.id,
                      ].join('__');
                      selectModifierInCartItem(
                        node,
                        menuItem.itemId,
                        modGroup.id,
                        true,
                        '',
                        modSymbolMapping,
                        menuRes.posSettings,
                        modality,
                        pathToModifier
                      );
                      handleModifierMatch(
                        cartItemSelectedItem,
                        menuItem,
                        pathToItem
                      );
                    }
                  });
                });
              }
            };

            if (autoCombinedCartItems[match.cartItemId]) {
              handleModifierMatch(
                getCartItemNonDummyNode(
                  autoCombinedCartItems[match.cartItemId]
                ),
                comboItem,
                pathToItem
              ); // Handle nested modifiers
            }

            const quantity = autoCombinedCartItemsQuantity[match.cartItemId];
            if (quantity > 1) {
              autoCombinedCartItemsQuantity[match.cartItemId] -= 1;
            } else {
              delete autoCombinedCartItems[String(match.cartItemId)];
              delete autoCombinedCartItemsQuantity[match.cartItemId];
            }
          });
          currSequenceId += 1;
          dispatch(setSequenceId(currSequenceId)); // Update the sequence id in cart state to avoid duplicate cart item id between auto combo items and cart items
          // Restart from beginning since cart state changed (items were removed)
          i = 0;
        } else {
          // Only increment if no combo was created
          i++;
        }
      }

      dispatch(
        setAutoCombinedCart({
          autoCombinedCartItems,
          autoCombinedCartItemsQuantity,
        })
      );

      // Trigger sendOrder after forming auto combo and there is at least one valid cart item
      const validAutoCombinedCartItems = Object.values(
        getValidCartItems(autoCombinedCartItems)
      );
      if (validAutoCombinedCartItems.length) dispatch(sendOrder({}));

      // Update the select item to be the regular cart item before auto combined item as it's not selectable
      const items = Object.values(autoCombinedCartItems);
      const firstAutoCombinedCartItemIdx = items.findIndex(
        (item) =>
          item.autoCombinedCartItemIds &&
          item.autoCombinedCartItemIds.length > 0
      );

      // HITL-1610: this recompute runs on every cart change, so under the flag
      // it may only move the pane when what the agent has open has stopped being
      // renderable. Off the flag the original condition stands alone.
      const mayMoveSelection =
        !selectIsShadowModeEnabled(getState()) ||
        selectIsSelectedItemUnopenable(getState());

      if (mayMoveSelection && firstAutoCombinedCartItemIdx >= 0) {
        const selectedIndex =
          firstAutoCombinedCartItemIdx === 0
            ? 0
            : firstAutoCombinedCartItemIdx - 1;
        const selectedItem = items[selectedIndex];
        dispatch(
          dialogActions.selectItemAndResetNavigation({
            item: selectedItem,
            itemCartId: selectedItem.cartItemId,
          })
        );
      }
    } catch (error) {
      logger.error({
        message: 'Form Auto Combo Failed',
        error: JSON.stringify(error),
      });
    }
  }
);

export const addItemToCart = createAsyncThunk(
  'menu/addItemToCart',
  async (
    {
      menuItem,
      quantity,
      addedBy = CartItemAdditionTypes.human,
      prefixWord,
      inputModSymbol,
      buildViaAutoCombo = false,
      byPassAutoPrompt = false,
      spokenPrompt,
      cartScope,
      upsellId,
      skipUpsellAttempt = false,
    }: IAddItemToCart,
    { dispatch, getState }
  ) => {
    // HITL-1619 — THE rule the whole write-path routing exists for.
    //
    // `addHypothesisItemToCart` (below, :1446 in the pre-routing numbering)
    // calls this same thunk on every changed hypothesis frame, and it runs in
    // `Shadow`. Resolve the target from the mode alone and the AI's rebuilt
    // items land in the agent's draft cart, while `cartSlice` — the cart that
    // becomes the order — quietly stops being rebuilt.
    //
    // `addedBy` is the discriminator, and it is already on every call: the AI's
    // replay passes `AI`, every human caller omits it and defaults to `human`.
    // So the AI is pinned to the main cart no matter what mode the agent is in,
    // and an explicit `cartScope` from a caller still cannot override that.
    const resolvedCartScope: CartScope =
      addedBy === CartItemAdditionTypes.AI
        ? MAIN_CART_SCOPE
        : cartScope ?? selectNewItemCartScope(getState());
    const scopedCartActions = cartActionsForScope(resolvedCartScope);

    const {
      menu: {
        menuRes,
        persistentVoiceProps,
        modSymbolMapping,
        unavailableItems,
        brandMenu,
        brandMenuItemsByName,
        unavailableItemsByName,
        restaurantMealPeriodsByName,
        greetingDetails,
      },
      restaurant: {
        selectedStage,
        selectedRestaurantDetails: {
          restaurantCode = '',
          primaryRestaurantCode = '',
          restaurantName = '',
          timezone,
        },
        isAIEnabled,
      },
    } = getState();

    // The new item's id comes from the SCOPED cart's own sequence. Taken from
    // `state.cart` while writing into the draft, it would mint an id the draft
    // has already used and overwrite one of the agent's own lines.
    const { modality, sequenceId: cartSequenceId } = cartStateForScope(
      getState(),
      resolvedCartScope
    );

    const isUnifiedMenu = getMenuUnificationFeatureFlag();

    if (menuRes) {
      const parsedMenuItem = isUnifiedMenu
        ? buildFullMenuUnifiedItem({
            item: menuItem,
            brandMenu,
            // Use primaryRestaurantCode for brand menu group lookups — in mirroring
            // scenarios the selected code differs from the code in restaurant_groups.
            restaurantCode: primaryRestaurantCode || restaurantCode,
            unavailableItemsByName,
            buildViaAutoCombo,
            timezone,
            restaurantMealPeriodsByName,
            brandMenuItemsByName,
            reuseBuild: selectIsMenuItemBuildCacheEnabled(getState()),
            lazyOptions: selectIsLazyOptionBuildEnabled(getState()),
          })
        : buildFullMenuItem(
            menuItem,
            menuRes,
            persistentVoiceProps,
            unavailableItems,
            selectedStage
          );
      const cartItem = {
        ...parsedMenuItem,
        cartItemId: cartSequenceId,
        childModifierGroups: {},
        modality,
        modcode: menuItem.modcode,
        addedBy,
        ...(isUnifiedMenu ? { readback: true } : null),
        ...(buildViaAutoCombo ? { isAutoCombined: true } : null),
        byPassAutoPrompt,
      };

      // handle default selected modifier
      processDefaultSelectedModifiers(
        cartItem,
        modSymbolMapping,
        menuRes.posSettings,
        modality,
        unavailableItemsByName
      );

      // If the item being added is the first one and greeting has an upsell item not withheld this session
      const isGreetingUpsell =
        isUnifiedMenu &&
        addedBy === CartItemAdditionTypes.human &&
        cartSequenceId === 1 &&
        greetingDetails?.constraint?.is_upsell_prompt;
      const isGreetingWithheld =
        isGreetingUpsell &&
        isConstraintWithheld(
          selectWithheldConstraints(getState()),
          greetingDetails.constraint.name
        );
      if (isGreetingWithheld) {
        logWithheldPromptSkipped(
          greetingDetails.constraint.name,
          'greeting upsell'
        );
      }
      if (isGreetingUpsell && !isGreetingWithheld) {
        handleGreetingUpsell(
          {
            ...greetingDetails,
            constraint: {
              ...greetingDetails.constraint,
              prompt: selectSpokenPrompt(
                getState(),
                { constraintName: greetingDetails.constraint.name },
                greetingDetails.constraint.prompt
              ),
            },
          },
          menuItem,
          !!isAIEnabled,
          dispatch,
          upsell,
          selectUpsellExperimentStamp(
            getState(),
            greetingDetails.constraint.name
          )
        );
      }

      const {
        config: {
          FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsellEnabled,
          FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE:
            doNotUpsellInExpressModeEnabled,
        },
      } = getState();
      const inExpressMealPeriod = isExpressModeActive(
        restaurantMealPeriodsByName,
        timezone
      );
      // Time-based express mode (hitl_express_timer): once the order has
      // elapsed past max_seconds, enter express mode — suppress only the
      // `do_not_upsell_in_express_mode`-tagged upsells, same as the express
      // meal period (HITL-1509).
      const expressTimerActive = selectIsExpressTimerActive(getState());
      // HITL-1799: the AI's own express mode counts too (flag-gated).
      const aiExpressActive = selectIsAIExpressUpsellSuppressionActive(
        getState()
      );
      const suppressUpsellsInExpressMode =
        addedBy !== CartItemAdditionTypes.AI &&
        ((doNotUpsellInExpressModeEnabled && inExpressMealPeriod) ||
          expressTimerActive ||
          aiExpressActive);
      if (suppressUpsellsInExpressMode) {
        logger.debug(
          `[EXPRESS_MODE] addItemToCart "${cartItem.name}" (cartItemId=${cartItem.cartItemId}, addedBy=${addedBy}): doNotUpsellInExpressModeEnabled=${doNotUpsellInExpressModeEnabled}, inExpressMealPeriod=${inExpressMealPeriod}, expressTimerActive=${expressTimerActive}, aiExpressActive=${aiExpressActive}`
        );
      }
      // HITL-1799: the AI's decline-count rules (cap, extreme express). Like
      // express above, never for an AI-added line.
      const aiUpsellSuppression =
        addedBy !== CartItemAdditionTypes.AI
          ? selectAIUpsellSuppression(getState())
          : undefined;

      dispatch(
        scopedCartActions.addItemToCart({
          cartItem,
          suppressUpsellsInExpressMode,
          suppressUpsellEnabled,
          withheldConstraints: selectWithheldConstraintNames(getState()),
          aiUpsellSuppression,
        })
      );

      // HITL-1610: under Supervised AI mode the centre pane is the agent's, and
      // an arriving frame from the AI must move neither the open item nor the
      // open modifier group — `selectItemAndResetNavigation` clears both.
      //
      // The mode is deliberately NOT read here. An earlier draft let the AI keep
      // the pane in a third `Supervise` mode and hand it over on the switch;
      // with `Supervise` cut, both remaining modes want the pane held, so the
      // rule is the flag alone. That also makes it true from the first frame of
      // a flag-on session rather than from the moment the agent presses
      // Intercept, which is the intent: the pane stops being yanked from under
      // them while they read, not only once they take over.
      //
      // Human adds are never gated — every human call site omits `addedBy`,
      // which defaults to `human`.
      if (
        addedBy !== CartItemAdditionTypes.AI ||
        !selectIsShadowModeEnabled(getState())
      ) {
        dispatch(
          dialogActions.selectItemAndResetNavigation({
            item: cartItem,
            itemCartId: cartSequenceId,
            // Which cart's item this id names. Without it the pane would open
            // `cartSlice`'s item of the same number and every subsequent pick
            // would go to the order.
            cartScope: resolvedCartScope,
          })
        );
      }

      // HITL-1738: adding an item no longer closes an open upsell — the
      // customer may still be answering it.

      if (quantity) {
        dispatch(
          scopedCartActions.updateQuantity({
            cartItemId: String(cartSequenceId),
            quantity: Math.min(quantity, 19),
          })
        );
      }

      if (prefixWord) {
        // handle prefixWord by selecting it as modifier if it is found
        Object.values(parsedMenuItem.modifierGroups).every((modGroup) => {
          const target = Object.values(modGroup.menuItems).find(
            (menuItem) =>
              menuItem.name.toLowerCase() === prefixWord.toLowerCase()
          );
          if (target) {
            dispatch(
              scopedCartActions.selectModifier({
                cartItemId: cartSequenceId,
                menuItemId: target.itemId,
                modGroupId: modGroup.id,
                selected: true,
                modCode: inputModSymbol || '',
                modSymbolMapping,
                posSettings: menuRes.posSettings,
                suppressUpsellEnabled,
                applyExpressCascade: suppressUpsellsInExpressMode,
                withheldConstraints: selectWithheldConstraintNames(getState()),
                aiUpsellSuppression,
              })
            );
            return false;
          }
          return true;
        });
      }

      dispatch(setFullMenuItem(parsedMenuItem));

      // Upsell attempt when a upsell item is added to the cart
      if (isUnifiedMenu && cartItem.name.includes('/upsell')) {
        let [intent, sendVal] = cartItem.name.replace('/', '').split(':');
        const constraints = Object.values(cartItem.modifierGroups);
        const constraintName =
          menuItem.activeConstraintName ?? constraints[0]?.name;
        // Withheld for this session's experiment group: neither asked nor attempted.
        if (
          isConstraintWithheld(
            selectWithheldConstraints(getState()),
            constraintName
          )
        ) {
          logWithheldPromptSkipped(constraintName, "'/upsell' add");
          return;
        }
        let slashSlot: IPromptSlot | undefined;
        if (!sendVal && menuItem !== undefined) {
          const { slashCommands } = menuItem;
          if (menuItem !== undefined && slashCommands) {
            slashSlot = slashCommandPromptSlot(menuItem);
            sendVal =
              (byPassAutoPrompt && spokenPrompt) ||
              selectSlashCommandSpokenPrompt(getState(), menuItem) ||
              '';
          }
        }
        let upsell_attempted;
        if (constraints.length > 0 && !skipUpsellAttempt) {
          upsell_attempted = upsell.attempt(
            constraints[0],
            '',
            UpsellTriggers.hitlOmnibar,
            sendVal,
            intent,
            upsellId,
            selectUpsellExperimentStamp(getState(), constraints[0].name),
            cartSequenceId
          );
        }
        // HITL-1612: never auto-prompt for an AI-origin add. The AI's replay
        // (`addHypothesisItemToCart`) adds `/upsell` items with `addedBy: AI` and
        // does not pass `byPassAutoPrompt`, so HITL spoke an upsell the AI had
        // already offered and then stamped `TTS_SENT` — which `ai.slice` reads to
        // clear `isAIAutoMode`, which stalls the AI's own cart replay at
        // `useHypothesisFrames` mid-order. No agent is involved in any of that.
        //
        // Keyed on `addedBy` rather than on the flag, because it is a defect
        // rather than a mode: `cartSlice.addItemToCart` already skips upsell
        // auto-decline for AI adds ("they replay an already-resolved order and
        // shouldn't trigger session-level effects") and the express-mode
        // suppression above already keys on `addedBy !== AI`. This is the same
        // rule applied to the one session-level effect that was still missing it.
        //
        // Deliberately the narrowest possible placement: `upsell.attempt`'s
        // metric is left running, so only the speech, its ask count and its
        // interception stamp are withheld.
        if (!byPassAutoPrompt && addedBy !== CartItemAdditionTypes.AI) {
          // HITL-1587: route through the shared spoken-prompt thunk — it
          // substitutes {total} like the omnibar send, skips outright (with a
          // METRIC) when the template needs a check total and none exists,
          // and records a spoken total so Complete doesn't repeat it.
          const { spoken } = dispatch(
            sendSpokenPrompt({
              message: sendVal,
              metadata: {
                intent,
                upsell_attempted,
                upsell_metric_version: UPSELL_METRIC_VERSION,
              },
              skipMetricMessage: `Upsell auto-prompt (${intent}) skipped: no check total for its {total} template`,
            }) as any
          );
          if (spoken) {
            if (slashSlot) {
              dispatch(promptAsked(slashSlot, menuItem.slashCommands));
            }
            dispatch(
              messagingActions.sendAgentInterception({
                data: {
                  type: AgentInterceptionType.TTS_SENT,
                },
              })
            );
          }
        }
      }
    }
  }
);

export const addHypothesisItemToCart = createAsyncThunk(
  'menu/addHypothesisItemToCart',
  async (
    {
      orderItem: rawOrderItem,
      skipFindInMenu,
    }: { orderItem: EntityMenuItem; skipFindInMenu: boolean },
    { dispatch, getState }
  ) => {
    // HITL-1675: AI-only content is filtered at this boundary, before it can
    // enter the cart — a dropped prompt carrier never renders a ghost card,
    // never reaches agent_cart events, and never ships in an order. The order
    // build keeps its own guard as the last line for other entry paths.
    const orderItem = sanitizeHypothesisOrderItem(rawOrderItem);
    if (!orderItem) {
      logger.info(
        `Dropping AI-only prompt carrier '${rawOrderItem.name}' from the hypothesis import (HITL-1675)`
      );
      return;
    }

    const {
      menu: {
        topLevelMenuItems,
        modSymbolMapping,
        codeNameMapping,
        menuRes,
        unavailableItemsByName,
        brandMenuItemsByName,
      },
      cart: { sequenceId: cartSequenceId },
      config: {
        FEATURE_FLAG_BM_AUTO_COMBO,
        FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsellEnabled,
      },
    } = getState();

    const isUnifiedMenu = getMenuUnificationFeatureFlag();
    const isModifierQuantityEnabled =
      isUnifiedMenu && selectIsModifierQuantityEnabled(getState());

    if (skipFindInMenu) {
      // No need to build the cart tree and just display the brand menu cart when HITL and AI are using different menu
      dispatch(
        addHypothesisCartItem({
          ...orderItem,
          cartItemId: cartSequenceId,
        })
      );
      return;
    }

    // Builds one node of the selection tree for cartActions.selectModifiers.
    // The whole tree is committed in a single dispatch: per-modifier
    // dispatches on items with large constraint sets (e.g. Crowd Pleasers)
    // each triggered a full store notification + render pass, which is what
    // lagged HITL during hypothesis replays (HITL-1402).
    const buildChildModSelection = ({
      mod,
      fullMainItem,
      currentCartItemId,
      modModifierGroupId,
      modQuantity,
    }: {
      mod: EntityMenuItem;
      fullMainItem: ParsedMenuItem | undefined;
      currentCartItemId: number;
      modModifierGroupId: string;
      modQuantity: number;
    }): IBatchedModifierSelection | null => {
      if (!fullMainItem) {
        logger.warn(
          `Cannot process modifier: fullMainItem is undefined for modifier group ${modModifierGroupId}`
        );
        return null;
      }

      //find the modifier group and modifier based on the modifier id
      const modifierId = mod.id !== -1 ? String(mod.id) : mod.name || '';
      if (!modifierId) {
        logger.warn(
          `Cannot find modifier: mod.id is -1 and mod.name is undefined for modifier group ${modModifierGroupId}`
        );
        return null;
      }

      let { modGroup, modifier } = getModifierAndModGroupFromHypotheses(
        fullMainItem,
        modifierId,
        modModifierGroupId
      );

      if (!modGroup || !modifier) {
        //find the modifier group and modifier based on the modifier name if they couldn't be found by id
        if (mod.name) {
          const { modGroup: modGroupByName, modifier: modifierByName } =
            getModifierAndModGroupFromHypothesesByName(
              fullMainItem,
              mod.name,
              modModifierGroupId
            );
          modGroup = modGroupByName;
          modifier = modifierByName;
        }
      }

      if (modifier && modGroup) {
        // HITL-1290: the parsed `available` flag covers out-of-stock and
        // meal-period-inactive options. The AI shouldn't propose either —
        // log and ignore the modifier until the AI handles it upstream.
        if (isUnifiedMenu && modifier.available === false) {
          logger.warn(
            `Ignoring unavailable modifier ${modifier.itemId} from hypothesis event`
          );
          return null;
        }

        const modCode = mod.pos_specific_properties?.modcode
          ? codeNameMapping[mod.pos_specific_properties.modcode]?.code
          : '';

        const pathToItem = modifier.pathToItem || '';
        //TODO handle the quantity of modifier when we have the quantity selection function in the future. Now the quantity should default to 1.
        return {
          selection: {
            cartItemId: currentCartItemId,
            menuItemId: modifier.itemId,
            modGroupId: modGroup.id,
            selected: true,
            modCode,
            modSymbolMapping,
            posSettings: menuRes?.posSettings || {},
            quantity: modQuantity,
            readback: mod.readback || false,
            pathToItem: modifier.pathToItem,
            suppressUpsellEnabled,
          },
          // The unavailable-constraint check must see selections applied
          // earlier in the batch, so it runs inside the reducer against the
          // evolving cart item rather than here.
          ...(isUnifiedMenu
            ? {
                unavailableConstraintCheck: {
                  parentModifier: getParentModifier(pathToItem),
                  modGroupName: modGroup.id,
                  itemName: modifier.itemId,
                },
              }
            : null),
          children:
            mod.children && mod.children.length > 0
              ? buildOrderItemChildrenSelections(
                  mod.children,
                  modifier,
                  currentCartItemId
                )
              : [],
        };
      } else {
        const modName = mod.name || 'unknown';
        logger.info(
          `Can't find the modifier ${modName} with id ${mod.id} or modifier group ${modModifierGroupId} based on the id or name from the hypotheses in the menu`
        );
        return null;
      }
    };

    const buildOrderItemChildrenSelections = (
      children: EntityMenuItem[],
      fullMainItem: ParsedMenuItem | undefined,
      currentCartItemId: number
    ): IBatchedModifierSelection[] => {
      if (!fullMainItem) {
        logger.warn(
          `Cannot handle order item children: fullMainItem is undefined`
        );
        return [];
      }

      const selections: IBatchedModifierSelection[] = [];

      // HITL-1758: with counts on, the AI's count survives the import.
      if (isModifierQuantityEnabled) {
        // HITL-1762: units already taken per option, so "sugar" sent as four
        // rows meets the same per-option cap as "sugar, quantity 4" — the
        // reducer adds rows up and would otherwise carry them past it.
        const unitsTaken: Record<string, number> = {};
        const fallbackPicks: Record<string, string> = {};
        const slotsFilled: Record<string, number> = {};
        // The item's own groups, so a slot family the menu defines constraint
        // by constraint is recognised as slots and takes no count.
        const levelGroups = fullMainItem?.modifierGroups
          ? Object.values(getOptionModifierGroups(fullMainItem))
          : [];
        children.forEach((mod) => {
          const groupId = resolveHypothesisModGroupId(
            fullMainItem,
            mod.modifier_group_id
          );
          const quantity = mod.quantity || 1;
          const modGroup = findModGroup(fullMainItem, groupId);
          // The front end's own `_1..N` split (a dozen) takes one unit per
          // slot, filled in order across rows: "2 plain + 1 sesame" arrives as
          // `plain → bagel_choice_1 ×2, sesame → bagel_choice_2` (the suffix
          // counts rows, not bagels), and lands plain, plain, sesame.
          const slotStem = findModGroup(fullMainItem, `${groupId}_1`)
            ? groupId
            : modGroup && isSplitSlotGroup(modGroup)
            ? modGroup.name
            : undefined;
          if (slotStem) {
            for (let unit = 0; unit < quantity; unit++) {
              const index = (slotsFilled[slotStem] ?? 0) + 1;
              const slotId = `${slotStem}_${index}`;
              if (!findModGroup(fullMainItem, slotId)) {
                logger.warn(
                  `[HITL-1758] AI sent more ${slotStem} units than the item has slots; "${
                    mod.name
                  }" x${quantity - unit} dropped`
                );
                break;
              }
              slotsFilled[slotStem] = index;
              const node = buildChildModSelection({
                mod,
                fullMainItem,
                currentCartItemId,
                modModifierGroupId: slotId,
                modQuantity: 1,
              });
              if (node) selections.push(node);
            }
            return;
          }
          // Rows that only reached this group through the fallback: a second,
          // different option in a pick-one group replaces the first in the
          // reducer, which used to be invisible (both were dropped before).
          if (groupId !== mod.modifier_group_id) {
            const earlier = fallbackPicks[groupId];
            if (
              earlier &&
              earlier !== mod.name &&
              modGroup?.maximumSelections === 1
            ) {
              logger.warn(
                `[HITL-1758] AI sent "${earlier}" and "${mod.name}" into pick-one group ${groupId}; keeping "${mod.name}"`
              );
            }
            fallbackPicks[groupId] = mod.name;
          }
          let modQuantity = quantity;
          if (modGroup) {
            const key = `${groupId}\u0000${mod.name}`;
            const room =
              getModifierMaxQuantity(modGroup, isUnifiedMenu, levelGroups) -
              (unitsTaken[key] ?? 0);
            if (room <= 0) return;
            modQuantity = clampModifierQuantity(quantity, room);
            unitsTaken[key] = (unitsTaken[key] ?? 0) + modQuantity;
          }
          const node = buildChildModSelection({
            mod,
            fullMainItem,
            currentCartItemId,
            modModifierGroupId: groupId,
            modQuantity,
          });
          if (node) selections.push(node);
        });
        return selections;
      }

      children.forEach((mod) => {
        if (mod.quantity > 1) {
          new Array(mod.quantity).fill(0).forEach((_, index) => {
            const node = buildChildModSelection({
              mod,
              fullMainItem,
              currentCartItemId,
              modModifierGroupId: mod.modifier_group_id + '_' + (index + 1),
              modQuantity: 1,
            });
            if (node) selections.push(node);
          });
        } else {
          const node = buildChildModSelection({
            mod,
            fullMainItem,
            currentCartItemId,
            modModifierGroupId: mod.modifier_group_id,
            modQuantity: mod.quantity || 1,
          });
          if (node) selections.push(node);
        }
      });
      return selections;
    };

    //use the first menu item with the id in the assumption of the same item will show up in the menu once
    let mainItem = Object.values(topLevelMenuItems).find((menuItem) =>
      orderItem.id !== -1
        ? String(orderItem.id) === menuItem.id
        : orderItem.name === menuItem.id
    );

    if (!mainItem) {
      // Find the item in menu by name if it couldn't be found by id
      mainItem = Object.values(topLevelMenuItems).find(
        (menuItem) => orderItem.name === menuItem.name
      );
    }

    // HITL-1742: the AI can order a brand menu item that sits under no
    // top-level constraint — "12 pack jalapeno poppers" is reachable only via
    // upsell / side-choice constraints. It is still a real item, so build it
    // from the brand menu instead of falling through to the "Not found" card.
    // brandMenuItemsByName holds the whole brand, so the store's not-sold
    // items — which getTopLevelItems leaves out — must stay out here too.
    // HITL-1742-1: so must informational-only items ("coupon offer" is the
    // AI's coupon prompt, not something to sell); they keep the card.
    if (
      !mainItem &&
      isUnifiedMenu &&
      orderItem.name &&
      !isBMItemNotSold(unavailableItemsByName, orderItem.name) &&
      !isBMItemInformationalOnly(brandMenuItemsByName[orderItem.name])
    ) {
      mainItem = findNonTopLevelMenuItem(orderItem.name, brandMenuItemsByName);
    }

    if (
      mainItem &&
      !(
        isUnifiedMenu &&
        isBMItemOutOfStock(unavailableItemsByName, mainItem.name)
      )
    ) {
      let currentCartItemId = cartSequenceId;
      const inputModSymbol = orderItem.pos_specific_properties?.modcode
        ? codeNameMapping[orderItem.pos_specific_properties.modcode]?.code
        : '';

      const isItemAutoCombined = !!(
        isUnifiedMenu &&
        FEATURE_FLAG_BM_AUTO_COMBO &&
        orderItem.is_auto_combined
      );
      //add the main item to cart
      await dispatch(
        addItemToCart({
          menuItem: mainItem,
          quantity: orderItem.quantity,
          addedBy: CartItemAdditionTypes.AI,
          inputModSymbol,
          buildViaAutoCombo: isItemAutoCombined,
        })
      );

      const {
        menu: { fullMenuItems },
        cart: { cartItems },
      } = getState();

      const itemId = mainItem.id + '-' + mainItem.categoryId;
      const fullMainItem = fullMenuItems[itemId];

      if (!fullMainItem) {
        logger.warn(
          `Cannot find fullMainItem for itemId: ${itemId} (mainItem: ${mainItem.name}, id: ${mainItem.id})`
        );
        // Still continue with quantity update and item selection, but skip modifier processing
      } else {
        const children = orderItem.children;
        if (children?.length > 0) {
          const selections = buildOrderItemChildrenSelections(
            children,
            fullMainItem,
            currentCartItemId
          );
          if (selections.length > 0) {
            dispatch(
              cartActions.selectModifiers({
                selections,
                unavailableItemsByName: isUnifiedMenu
                  ? unavailableItemsByName
                  : undefined,
              })
            );
          }
        }
      }

      let quantity = orderItem.quantity ? Number(orderItem.quantity) : 1;

      if (quantity > 1) {
        dispatch(
          cartActions.updateQuantity({
            cartItemId: currentCartItemId.toString(),
            quantity: Math.min(quantity, 19),
          })
        );
      }
      // HITL-1610: this whole thunk IS the AI's replay, so there is no `addedBy`
      // to check — the flag alone decides. Under Supervised AI mode the open item
      // and the open modifier group survive the frame, in both modes.
      if (!selectIsShadowModeEnabled(getState())) {
        dispatch(
          dialogActions.selectItemAndResetNavigation({
            item: cartItems[currentCartItemId],
            itemCartId: currentCartItemId,
          })
        );
      }
    } else {
      logger.info(
        `Can't find the main item based on the id: ${orderItem.id} or name: ${orderItem.name} from the hypotheses in the menu`
      );
      dispatch(
        addHypothesisCartItem({
          ...orderItem,
          cartItemId: cartSequenceId,
        })
      );
    }
  }
);

export const fetchMenuVersions = createAsyncThunk(
  'menu/menuVersions',
  async (
    {
      restaurantCode: restaurant_code,
      logout,
    }: { restaurantCode: string; logout: Function },
    { getState }
  ) => {
    const {
      config: { MENU_API },
    } = getState();
    let filteredMenuVersions: IMenuVersion[] = [];
    let prodLiveVersion = {} as IMenuVersion;
    if (restaurant_code) {
      try {
        const menuVersions: { data: IMenuVersionsResponse[] } =
          await getMenuVersionsFromMenuAPI(MENU_API, {
            restaurant_code,
          });
        const { LIVE, PLAYGROUND } = MenuStages;
        const versionMapping = (menuVersions?.data || []).reduce(
          (
            list,
            {
              commit_id: commitId,
              created_at: createdAt,
              creator_first_name,
              creator_last_name,
              publisher_username: publisherUsername,
              publisher_first_name,
              publisher_last_name,
              creator_username: creatorUsername,
              id,
              stage,
              is_active: isActive,
              updated_at: updatedAt,
              comment,
            }
          ) => {
            const versionData = {
              commitId,
              createdAt,
              creatorUsername,
              creatorName: `${creator_first_name} ${creator_last_name}`,
              publisherUsername,
              publisherName: `${publisher_first_name} ${publisher_last_name}`,
              id,
              stage,
              isActive,
              updatedAt,
              comment,
            };
            if (stage.toUpperCase() === LIVE && isActive) {
              prodLiveVersion = versionData;
            }
            if (stage.toUpperCase() === PLAYGROUND) {
              list[commitId] = versionData;
            }
            return list;
          },
          {} as Record<string, IMenuVersion>
        );
        filteredMenuVersions = Object.values(versionMapping).sort(
          (a, b) => Number(b.commitId) - Number(a.commitId)
        );
      } catch (error) {
        if (getAuthRefactorFeatureFlag()) {
          logout('Error while fetching menu versions. Logging out');
        }
      }
    }
    return {
      menuVersions: [
        Object.assign({}, defaultMenuVersion),
        ...filteredMenuVersions,
      ],
      prodLiveVersion,
    };
  }
);

export const selectMenuVersion = createAsyncThunk(
  'menu/selectMenuVersion',
  async (
    {
      currentMenuVersion,
      logout,
    }: { currentMenuVersion: string; logout: Function },
    { getState, dispatch }
  ) => {
    const {
      restaurant: {
        selectedRestaurantDetails: { timezone, restaurantCode },
        selectedStage: currentStage,
      },
    } = getState();

    if (restaurantCode) {
      dispatch(startLoading({}));
      dispatch(
        fetchMenu({
          currentMenuVersion,
          timezone,
          restaurantCode,
          currentStage,
          logout,
        })
      );
    }
    return currentMenuVersion;
  }
);

export const replaceWithComboItem = createAsyncThunk(
  'menu/replaceWithComboItem',
  async (
    {
      originalCartItem,
      targetItemName,
      comboSize,
      optionsToPreSelect,
      // HITL-1617: which cart holds `originalCartItem`. This thunk DELETES it
      // and adds a rebuilt one, so an unscoped run against a My Cart line would
      // destroy the AI's item of the same id — both carts number from 1. Default
      // `main` keeps every pre-existing caller on today's path.
      cartScope = MAIN_CART_SCOPE,
    }: {
      originalCartItem: CartItem;
      targetItemName: string;
      comboSize?: ConversionSize;
      optionsToPreSelect?: string[];
      cartScope?: CartScope;
    },
    { dispatch, getState }
  ) => {
    const scopedCartActions = cartActionsForScope(cartScope);

    // Step 1: Get required state from Redux store
    const {
      menu: {
        topLevelMenuItems,
        menuRes,
        modSymbolMapping,
        brandMenu,
        brandMenuItemsByName,
        itemConversionBusinessRules,
        unavailableItemsByName,
        restaurantMealPeriodsByName,
      },
      restaurant: {
        selectedRestaurantDetails: {
          restaurantCode = '',
          primaryRestaurantCode = '',
          timezone,
        },
      },
      config: {
        FEATURE_FLAG_ITEM_CONVERSION: isItemConversionEnabled,
        FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsellEnabled,
        FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE:
          doNotUpsellInExpressModeEnabled,
      },
    } = getState();

    // HITL-1617: the new item's id comes from the SCOPED cart's sequence. Taking
    // it from `state.cart` while writing into the draft would mint an id the
    // draft has already used and silently overwrite one of the agent's lines.
    const { modality, sequenceId: cartSequenceId } = cartStateForScope(
      getState(),
      cartScope
    );

    // Express mode (meal period or elapsed hitl_express_timer) suppresses only
    // the `do_not_upsell_in_express_mode`-tagged upsells (HITL-1509). AI-added
    // items are still skipped inside cartSlice.
    const suppressUpsellsInExpressMode =
      (doNotUpsellInExpressModeEnabled &&
        isExpressModeActive(restaurantMealPeriodsByName, timezone)) ||
      selectIsExpressTimerActive(getState()) ||
      selectIsAIExpressUpsellSuppressionActive(getState());
    // HITL-1799: the AI's decline-count rules (cap, extreme express).
    const aiUpsellSuppression = selectAIUpsellSuppression(getState());

    // Step 2 & 3: Find and build the target substitute item (top-level or non-top-level)
    const substituteResult = buildSubstituteItem({
      targetItemName,
      topLevelMenuItems,
      brandMenu,
      brandMenuItemsByName,
      // Use primaryRestaurantCode for brand menu group lookups — in mirroring
      // scenarios the selected code differs from the code in restaurant_groups,
      // and a bare selected code resolves to no groups at all, which drops every
      // group- or meal-period-scoped constraint from the converted item.
      restaurantCode: primaryRestaurantCode || restaurantCode,
      unavailableItemsByName,
      timezone,
      restaurantMealPeriodsByName,
      reuseBuild: selectIsMenuItemBuildCacheEnabled(getState()),
      lazyOptions: selectIsLazyOptionBuildEnabled(getState()),
    });

    if (!substituteResult) {
      return;
    }

    const { parsedItem: parsedTargetItem, topLevelMenuItem: targetMenuItem } =
      substituteResult;

    const newTargetCartItem: CartItem = {
      ...parsedTargetItem,
      cartItemId: cartSequenceId,
      childModifierGroups: { ...originalCartItem.childModifierGroups }, // Preserve existing modifiers initially
      modality,
      modcode: targetMenuItem?.modcode,
      addedBy: CartItemAdditionTypes.human,
      readback: true,
      readbackName:
        targetMenuItem?.readbackName ||
        brandMenuItemsByName[targetItemName]?.readback_name ||
        '',

      // Preserve user-specific data from original item
      itemLevelMemo: originalCartItem.itemLevelMemo,
      quantity: originalCartItem.quantity,
      isUpsell: originalCartItem.isUpsell,
      isUpsellPrompt: originalCartItem.isUpsellPrompt,
      label: originalCartItem.label,
      isConverted: true, // Mark this item as converted
    } as CartItem;

    // Step 4: Filter and preserve compatible modifiers from original item
    // Only keep modifiers that exist in both original and target items
    if (
      originalCartItem.childModifierGroups &&
      newTargetCartItem.modifierGroups
    ) {
      const targetModifierGroupNames = Object.values(
        newTargetCartItem.modifierGroups
      ).map((group) => group.name?.toLowerCase());

      const compatibleModifierGroups: { [key: string]: any } = {};
      Object.entries(originalCartItem.childModifierGroups).forEach(
        ([key, modifierGroup]) => {
          const modifierGroupName = modifierGroup.name?.toLowerCase();
          if (
            modifierGroupName &&
            targetModifierGroupNames.includes(modifierGroupName)
          ) {
            compatibleModifierGroups[key] = modifierGroup;
          }
        }
      );

      // Update the cart item with only compatible modifiers
      newTargetCartItem.childModifierGroups = compatibleModifierGroups;
    }

    // Step 5: Replace items in cart (delete original, add new target item)
    dispatch(
      scopedCartActions.deleteCartItem(originalCartItem.cartItemId.toString())
    );

    dispatch(
      scopedCartActions.addItemToCart({
        cartItem: newTargetCartItem,
        suppressUpsellsInExpressMode,
        suppressUpsellEnabled,
        withheldConstraints: selectWithheldConstraintNames(getState()),
        aiUpsellSuppression,
      })
    );

    // Register the target item in the loaded menu, the same way addItemToCart
    // does — anything that later looks a cart item up by its menu entry (via
    // `fullMenuItems[itemId]`) cannot find a converted item otherwise.
    dispatch(setFullMenuItem(parsedTargetItem));

    // Step 6: Pre-select modifiers based on conversion type (e.g., combo_size)
    if (newTargetCartItem.modifierGroups && optionsToPreSelect) {
      // Pre-select each option specified in the conversion info
      // Search through all modifier groups to find one that contains the option
      for (const optionName of optionsToPreSelect) {
        const targetModifierGroup = Object.values(
          newTargetCartItem.modifierGroups
        ).find((modGroup) =>
          Object.values(modGroup.menuItems).some(
            (item) => item.name === optionName
          )
        );

        if (targetModifierGroup) {
          const targetMenuItem = Object.values(
            targetModifierGroup.menuItems
          ).find((item) => item.name === optionName);

          if (targetMenuItem) {
            // Select the modifier
            dispatch(
              scopedCartActions.selectModifier({
                cartItemId: newTargetCartItem.cartItemId,
                menuItemId: targetMenuItem.itemId,
                modGroupId: targetModifierGroup.id,
                selected: true,
                modCode: '',
                modSymbolMapping,
                posSettings: menuRes?.posSettings || {},
                readback: true,
                suppressUpsellEnabled,
                applyExpressCascade: suppressUpsellsInExpressMode,
                withheldConstraints: selectWithheldConstraintNames(getState()),
                aiUpsellSuppression,
              })
            );

            // Step 7: Apply option replacement if triggered by the pre-selected modifier
            // Only run option replacement if both unified menu and item conversion feature flags are enabled
            const isUnifiedMenu = getMenuUnificationFeatureFlag();
            if (isUnifiedMenu && isItemConversionEnabled) {
              // Extract option replacement business rules (default size selection + combo size transformation)
              const optionReplacementRules: IBusinessRule[] = [
                ...itemConversionBusinessRules.defaultSizeSelection,
                ...itemConversionBusinessRules.comboSizeTransformation,
              ];

              // `newTargetCartItem` is the snapshot built before the pre-select
              // dispatch above, so it does not reflect the option we just selected.
              // Re-read the freshly-updated cart item from the store so availability
              // checks (e.g. the dependent side/drink sizes for the just-selected
              // combo size) evaluate against the new selection, not the prior state.
              const latestTargetCartItem =
                (newTargetCartItem &&
                  cartItemsForScope(getState(), cartScope)?.[
                    newTargetCartItem.cartItemId
                  ]) ??
                newTargetCartItem;

              const replacementInfo = analyzeOptionReplacement(
                targetModifierGroup.name || '',
                optionName,
                latestTargetCartItem,
                optionReplacementRules,
                unavailableItemsByName
              );

              if (replacementInfo.shouldApplyReplacement) {
                dispatch(
                  scopedCartActions.applyOptionReplacement({
                    cartItemId: latestTargetCartItem.cartItemId,
                    targetModifierGroups: replacementInfo.targetModifierGroups,
                    unavailableItemsByName,
                  })
                );
              }
            }
          }
        }
      }
    }

    // Step 8: Log the conversion and focus on the new target item
    logger.debug(
      `Item conversion: ${originalCartItem.name} -> ${newTargetCartItem.name}${
        comboSize ? ` (${comboSize})` : ''
      } with preserved modifiers`
    );

    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: newTargetCartItem,
        itemCartId: cartSequenceId,
        // Stay on the cart we just converted in. Without this the pane would
        // jump to `cartSlice`'s item of the same number.
        cartScope,
      })
    );

    return {
      originalCartItemId: originalCartItem.cartItemId,
      newTargetCartItem,
      comboSize,
    };
  }
);

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    considerTimePeriods: (
      state,
      action: PayloadAction<{
        modality: ModalityType;
        timezone: string;
        stage: string;
      }>
    ) => {
      if (state.categoriesWithTimePeriod.length && state.menuRes) {
        const { timezone, stage } = action.payload;
        const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
          state.categoriesWithTimePeriod,
          timezone
        );
        state.availableCategoryWithTimePeriod =
          categoriesWithActiveTimePeriod.filter(
            (category) => !category.inactive
          );

        const { menuItems, overrides, menuItemSettings, posSettings } =
          state.menuRes;
        const { topLevelMenuItems, codeNameMapping, modSymbolMapping } =
          parseMenuResponse({
            categories: [
              ...categoriesWithActiveTimePeriod.filter(
                (category) => category.name !== AUTO_COMBOS
              ),
              ...state.alwaysAvailableCategories,
            ],
            menuRes: { menuItems, overrides, menuItemSettings, posSettings },
            persistentVoiceProps: state.persistentVoiceProps,
            unavailableItems: state.unavailableItems,
            stage,
          });
        state.topLevelMenuItems = topLevelMenuItems;
        state.codeNameMapping = codeNameMapping;
        state.modSymbolMapping = modSymbolMapping;
      }
    },
    considerTimePeriodsForUnifiedMenu: (
      state,
      action: PayloadAction<{
        timezone: string;
        // HITL-1749: selectInfoSlashCommandOptions; omitted = off.
        infoSlashCommands?: InfoSlashCommandOptions;
      }>
    ) => {
      if (state.categoriesWithTimePeriod.length && state.brandMenu) {
        const { timezone, infoSlashCommands = null } = action.payload;
        const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
          state.categoriesWithTimePeriod,
          timezone
        );
        state.availableCategoryWithTimePeriod =
          categoriesWithActiveTimePeriod.filter(
            (category) => !category.inactive
          );

        state.topLevelMenuItems = withInfoSlashCommands(
          getTopLevelItems(
            [
              ...categoriesWithActiveTimePeriod.filter(
                (category) => category.name !== AUTO_COMBOS
              ),
              ...state.alwaysAvailableCategories,
            ],
            convertToMapUsingName(state.brandMenu.items),
            state.unavailableItemsByName,
            convertToMapUsingName(state.brandMenu.constraints),
            timezone,
            state.restaurantMealPeriodsByName,
            state.currentRestaurantGroups
          ),
          infoSlashCommands,
          {
            brandMenuItems: state.brandMenu.items ?? [],
            unavailableItemsByName: state.unavailableItemsByName,
            timezone,
            restaurantMealPeriodsByName: state.restaurantMealPeriodsByName,
          }
        );
      }
    },
    setFullMenuItem: (state, action: PayloadAction<ParsedMenuItem>) => {
      const item = action.payload;
      const itemId = item.itemId;
      state.fullMenuItems[itemId] = item;
    },
    updateSelectedMenuVersion: (state, { payload }: PayloadAction<string>) => {
      state.selectedMenuVersion = payload;
    },
    resetMenu: (state) => {
      state.topLevelMenuItems = {};
      state.modSymbolMapping = {};
      state.codeNameMapping = {};
      state.menuRes = undefined;
      state.persistentVoiceProps = {};
      state.categoriesWithTimePeriod = [];
      state.availableCategoryWithTimePeriod = [];
      state.alwaysAvailableCategories = [];
      state.unavailableItems = {};
      state.bmItemsSynonyms = undefined;
      state.loadedVersionDetails = null;
      state.restaurantMealPeriodsByName = {};
      state.currentRestaurantGroups = [];
    },
    updateMenuItems: (state, action) => {
      const {
        topLevelMenuItems,
        processedCategoryAndTimePeriodJSON,
        processedMenuJSON,
        processedModGroupsJSON,
        categoriesWithTimePeriod,
        availableCategoryWithTimePeriod,
        alwaysAvailableCategories,
        persistentVoiceProps,
        codeNameMapping,
        modSymbolMapping,
        unavailableItems,
        autoComboPrpIds,
        comboPrpIdToALaCartePrpId,
        brandMenu,
        bmItemsSynonyms,
        restaurantUnavailableEntities,
        unavailableItemsByName,
        restaurantMealPeriodsByName,
        currentRestaurantGroups,
        itemConversionBusinessRules,
        greetingDetails,
        intentCards,
      } = action.payload;
      state.topLevelMenuItems = topLevelMenuItems;
      logger.info({
        message: 'Updating top level menu items',
        itemCount: Object.keys(topLevelMenuItems).length,
      });
      state.modSymbolMapping = modSymbolMapping;
      state.codeNameMapping = codeNameMapping;
      state.menuRes = {
        ...processedCategoryAndTimePeriodJSON,
        ...processedMenuJSON,
        ...processedModGroupsJSON,
      };
      state.persistentVoiceProps = persistentVoiceProps;
      state.categoriesWithTimePeriod = categoriesWithTimePeriod;
      state.availableCategoryWithTimePeriod = availableCategoryWithTimePeriod;
      state.alwaysAvailableCategories = alwaysAvailableCategories;
      state.unavailableItems = unavailableItems;
      state.autoComboPrpIds = autoComboPrpIds;
      state.comboPrpIdToALaCartePrpId = comboPrpIdToALaCartePrpId;
      state.brandMenu = brandMenu;
      state.brandMenuItemsByName = convertToMapUsingName(
        brandMenu?.items || []
      );
      state.itemConversionBusinessRules = itemConversionBusinessRules;
      state.bmItemsSynonyms = bmItemsSynonyms;
      state.restaurantUnavailableEntities = restaurantUnavailableEntities;
      state.unavailableItemsByName = unavailableItemsByName;
      state.restaurantMealPeriodsByName = restaurantMealPeriodsByName || {};
      state.currentRestaurantGroups = currentRestaurantGroups || [];
      state.greetingDetails = greetingDetails || undefined;
      state.intentCards = intentCards || undefined;
    },
    updateIntentCards: (state, action: PayloadAction<IUtterance[]>) => {
      state.intentCards = buildIntentCardsFromBM(action.payload) || undefined;
      state.dismissReadbackPhrases =
        getDismissReadbackPhrasesFromBM(action.payload) || undefined;
    },
    resetVersions: (state) => {
      state.menuVersions = [];
      state.prodLiveVersion = {} as IMenuVersion;
    },
    updateLoadedVersionDetails: (
      state,
      { payload }: PayloadAction<LoadedVersionDetails | null>
    ) => {
      state.loadedVersionDetails = payload;
    },
    updateMenuLoadingTimings: (
      state,
      { payload }: PayloadAction<MenuLoadingTimings>
    ) => {
      state.menuLoadingTimings = { ...state.menuLoadingTimings, ...payload };
    },
    resetMenuLoadingTimings: (state) => {
      state.menuLoadingTimings = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchMenuVersions.fulfilled,
      (state, { payload: { menuVersions, prodLiveVersion } }) => {
        state.menuVersions = menuVersions;
        state.prodLiveVersion = prodLiveVersion;
      }
    );
    builder.addCase(selectMenuVersion.fulfilled, (state, action) => {
      state.selectedMenuVersion = action.payload;
    });
  },
});

export const menuActions = menuSlice.actions;
export const {
  considerTimePeriods,
  setFullMenuItem,
  updateSelectedMenuVersion,
  updateMenuItems,
  updateIntentCards,
  resetMenu,
  resetVersions,
  considerTimePeriodsForUnifiedMenu,
  updateLoadedVersionDetails,
  updateMenuLoadingTimings,
  resetMenuLoadingTimings,
} = menuSlice.actions;

export default menuSlice.reducer;
